import UserApi from '../../../../rest-api/user-api';

/**
 * This function encapsulates the multi-step process of:
 *  1) Creating a patient if needed
 *  2) Generating presigned URLs
 *  3) Uploading the JSON/PNG to S3
 *  4) Updating the whiteboard DB record
 *
 * @param {string} bearerToken
 * @param {Object} saveData - The data needed to save the whiteboard
 * @param {Function} showSnackbar - A callback to display UI messages
 * @returns {Object} The final whiteboard record or similar response
 * @throws {Error} If any step fails
 */
export async function handleCreateOrUpdateWhiteboard(bearerToken, saveData, showSnackbar) {
    console.warn('Data sent to handleCreateOrUpdateWhiteboard: ', saveData);

    let patientId = saveData.patientId;

    // 1) If patientId is empty, optionally create a new patient
    if (!patientId && saveData.patientName) {
        const newPatientData = {
            patientName: saveData.patientName,
            patientSurname: saveData.patientSurname,
        };

        if (saveData.patientEmail) newPatientData.patientEmail = saveData.patientEmail;
        if (saveData.clinicPatientId) newPatientData.clinicPatientId = saveData.clinicPatientId;

        try {
            console.log('Creating a new patient with data:', newPatientData);
            const patientResp = await UserApi.createOrUpdatePatient(bearerToken, newPatientData);

            if (!patientResp?.patientId) {
                console.error('Failed to create patient: Unexpected response', patientResp);
                showSnackbar('Failed to create patient. The response did not contain a valid patientId.', 'error');
                throw new Error('Failed to create patient. Invalid response.');
            }

            patientId = patientResp.patientId;
            console.log(`New patient created with ID: ${patientId}`);
        } catch (error) {
            console.error('Error creating patient:', error);
            showSnackbar('Failed to create patient. Please try again.', 'error');
            throw error;
        }
    }

    // 2) Request presigned URLs for the JSON & PNG
    const fileKeyBase = `${saveData.userId}/${saveData.handoutId ? saveData.handoutId : 'GENERAL'}/${saveData.fileName}`;
    const payload = {
        files: [
            {
                key: `json/${fileKeyBase}.json`,
                fileContentType: 'application/json',
                fileSize: new Blob([saveData.canvasData]).size,
                method: 'put',
            },
            {
                key: `png/${fileKeyBase}.png`,
                fileContentType: 'image/png',
                fileSize: saveData.mergedImageBlob.size,
                method: 'put',
            },
        ],
        contentCategory: 'whiteboard',
    };

    let presignedResp;
    try {
        presignedResp = await UserApi.generatePresignedUrls(bearerToken, payload);
        if (!presignedResp?.putUrls?.length) {
            console.error('No presigned URLs received!');
            showSnackbar('Failed to get upload URLs from the server.', 'error');
            return;
        }
    } catch (err) {
        console.error('Error generating presigned URLs:', err);
        showSnackbar('Failed to get upload URLs from the server.', 'error');
        throw err;
    }

    let whiteboardETag = null;

    // 3) Upload files to S3 using the presigned POST URLs
    for (const fileData of presignedResp.putUrls) {
        const { presignedUrl, fields } = fileData;
        if (!presignedUrl) {
            console.error('Missing presigned URL in response', fileData);
            continue;
        }

        // Determine which blob to upload
        const isJsonFile = fields.key.endsWith('.json');
        const contentType = isJsonFile ? 'application/json' : 'image/png';
        const fileBody = isJsonFile
            ? new Blob([saveData.canvasData], { type: 'application/json' })
            : saveData.mergedImageBlob;

        // Build the form data for the POST request
        // Tested different approches, but seems formData works best
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append('Content-Type', contentType);
        formData.append('file', fileBody);

        try {
            const uploadResp = await fetch(presignedUrl, {
                method: 'POST',
                body: formData,
            });

            if (!uploadResp.ok) {
                throw new Error(`S3 Upload Failed: ${uploadResp.status} - ${uploadResp.statusText}`);
            }

            console.warn(`Successfully uploaded: ${fields.key}`);

            // Extract eTag for the PNG file
            if (!isJsonFile) {
                whiteboardETag = uploadResp.headers.get('ETag'); //TODO cappital E?
                console.warn(`Extracted eTag for PNG: ${whiteboardETag}`);
            }
        } catch (err) {
            console.error(`Upload failed for ${fields.key}`, err);
            showSnackbar(`File upload failed: ${fields.key}. Please try again.`, 'error');
            throw err;
        }
    }

    // 4) Finally, update the DB row with references to the new JSON/PNG
    const updatePayload = {
        fileName: saveData.fileName,
        notes: saveData.notes,
        jsonKey: `json/${fileKeyBase}.json`,
        pngKey: `png/${fileKeyBase}.png`,
        handoutId: saveData.handoutId || null,
        handoutContentName: saveData.handoutContentName || null,
        handoutAlphabeticalName: saveData.handoutAlphabeticalName || null,
        handoutDocumentTitle: saveData.handoutDocumentTitle || null,
        updatedById: saveData.updatedById || null,
        updatedByFirstName: saveData.updatedByFirstName || null,
        updatedByLastName: saveData.updatedByLastName || null,
        whiteboardStatus: saveData.whiteboardStatus,
        eTag: whiteboardETag,
        lastUsedColours: saveData.lastUsedColours || null,
    };

    if (patientId) updatePayload.patientId = patientId;
    if (saveData.patientName) updatePayload.patientName = saveData.patientName;
    if (saveData.patientSurname) updatePayload.patientSurname = saveData.patientSurname;
    if (saveData.patientEmail) updatePayload.patientEmail = saveData.patientEmail;
    if (saveData.clinicPatientId) updatePayload.clinicPatientId = saveData.clinicPatientId;
    if (saveData.diagramVariant) updatePayload.diagramVariant = saveData.diagramVariant;

    console.warn('Final Update Payload ');

    try {
        const updateResp = await UserApi.createOrUpdateWhiteboard(bearerToken, updatePayload);
        console.warn('Whiteboard DB update success');
        return {
            whiteboardId: updateResp.whiteboardId || null,
            canvasImageLink: `png/${fileKeyBase}.png`,
            canvasJsonLink: `json/${fileKeyBase}.json`,
            eTag: whiteboardETag,
            patientId: patientId || null,
            updatedByFirstName: saveData.updatedByFirstName || null,
            updatedByLastName: saveData.updatedByLastName || null,
            diagramVariant: saveData.diagramVariant || null,
        };
    } catch (err) {
        console.error('Whiteboard DB update failed:', err);
        showSnackbar('Whiteboard DB update failed. Please try again.', 'error');
        throw err;
    }
}
